import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Loader2, Power } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { KnowledgeBase } from "./KnowledgeBase";
import { useAuth } from "./AuthContext";

const REASON = { off: "AI answers are switched off. Visitors see guided options.", ceiling: "The absolute server budget ceiling is used up.",
  monthly: "This month's limit is reached.", daily: "Today's budget limit is reached.", messages: "Today's message cap is reached." };
const card = "rounded-2xl border border-white/10 bg-[#141110] p-5";
const inputCls = "mt-1 h-10 w-full rounded-xl border border-white/10 bg-[#0C0A09] px-3 text-sm text-stone-100 outline-none focus:border-orange-500";
const usd = (v) => `$${Number(v ?? 0).toFixed(4)}`;

const Status = ({ d }) => {
  const on = d.effective.ai_answering;
  return (
    <div className={`${card} flex flex-wrap items-start gap-3`} data-testid="ai-admin-status">
      {on ? <CheckCircle2 className="h-5 w-5 text-emerald-300" /> : <AlertTriangle className="h-5 w-5 text-amber-300" />}
      <div className="min-w-0 flex-1 text-sm">
        <p className="font-semibold text-stone-100" data-testid="ai-admin-state">{on ? "AI answers are LIVE" : "AI answers are OFF (guided mode)"}</p>
        {!on && <p className="mt-0.5 text-stone-400">{REASON[d.effective.reason] ?? ""}</p>}
        <p className="mt-1 text-xs text-stone-400">Server permission: {d.server.allow ? "allowed" : "blocked"} · Universal Key: {d.server.key_present ? "present" : "missing"} ·
          Absolute ceiling (server setting, not editable here): <strong className="text-stone-200">${d.server.ceiling_usd.toFixed(2)}</strong></p>
      </div>
    </div>
  );
};

const Field = ({ label, hint, children }) => (
  <label className="block text-xs text-stone-400">{label}{children}{hint && <span className="mt-1 block text-[11px] text-stone-500">{hint}</span>}</label>
);

const Settings = ({ d, onSaved }) => {
  const { can } = useAuth();
  const manage = can("ai.manage");
  const [f, setF] = useState(d.settings);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF(d.settings); }, [d.settings]);
  const num = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value === "" ? "" : Number(e.target.value) }));
  const model = d.models.find((m) => m.id === f.model);
  const save = async (next) => {
    if (next.enabled && !d.settings.enabled && !window.confirm("Switch AI answers ON? Visitors' messages will be answered by the AI and billed to the Universal Key within the limits shown.")) return;
    setBusy(true);
    toast.dismiss();
    try { await api.put("/admin/concierge/settings", next); toast.success("AI settings saved"); onSaved(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  return (
    <div className={card} data-testid="ai-admin-settings">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-display text-base text-stone-100">Settings{!manage && <span className="ml-2 text-xs text-stone-400" data-testid="ai-admin-readonly">(read-only: only a Super Administrator can change these)</span>}</p>
        {manage && <button type="button" disabled={busy} onClick={() => save({ ...f, enabled: !d.settings.enabled })} data-testid="ai-admin-toggle"
          className={`inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold ${d.settings.enabled ? "bg-emerald-400/15 text-emerald-200" : "bg-white/10 text-stone-200"}`}>
          <Power className="h-4 w-4" /> {d.settings.enabled ? "AI answers ON, switch off" : "AI answers OFF, switch on"}
        </button>}
      </div>
      <fieldset disabled={!manage} className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Model" hint={model ? `Ceiling price $${model.price_in_per_m}/M in, $${model.price_out_per_m}/M out${model.verified ? "" : " · not yet tested: cannot be switched on"}` : ""}>
          <select value={f.model} onChange={(e) => setF((x) => ({ ...x, model: e.target.value }))} className={inputCls} data-testid="ai-admin-model">
            {d.models.map((m) => <option key={m.id} value={m.id}>{m.label}{m.verified ? "" : " (untested)"}</option>)}
          </select>
        </Field>
        <Field label="Daily budget (US$)"><input type="number" step="0.05" min="0" value={f.daily_budget_usd} onChange={num("daily_budget_usd")} className={inputCls} data-testid="ai-admin-daily-budget" /></Field>
        <Field label="Monthly budget (US$)"><input type="number" step="0.5" min="0" value={f.monthly_budget_usd} onChange={num("monthly_budget_usd")} className={inputCls} data-testid="ai-admin-monthly-budget" /></Field>
        <Field label="AI messages per day"><input type="number" min="0" value={f.daily_message_cap} onChange={num("daily_message_cap")} className={inputCls} data-testid="ai-admin-message-cap" /></Field>
        <Field label="Turns per conversation"><input type="number" min="4" max="30" value={f.session_turns} onChange={num("session_turns")} className={inputCls} data-testid="ai-admin-session-turns" /></Field>
        <Field label="First-response timeout (s)"><input type="number" min="5" max="60" value={f.first_token_timeout_s} onChange={num("first_token_timeout_s")} className={inputCls} data-testid="ai-admin-first-timeout" /></Field>
        <Field label="Total timeout (s)"><input type="number" min="10" max="120" value={f.total_timeout_s} onChange={num("total_timeout_s")} className={inputCls} data-testid="ai-admin-total-timeout" /></Field>
      </fieldset>
      <p className="mt-3 text-xs text-stone-400" data-testid="ai-admin-effective-limits">
        Effective limits now: ${d.effective.limits.daily.toFixed(2)}/day · ${d.effective.limits.monthly.toFixed(2)}/month · ${d.effective.limits.ceiling.toFixed(2)} absolute ceiling. Lower of your limit and the ceiling always applies.
      </p>
      {manage && <button type="button" disabled={busy} onClick={() => save({ ...f, enabled: d.settings.enabled })} data-testid="ai-admin-save"
        className="tc-btn-primary mt-4 inline-flex h-10 items-center gap-2 rounded-full px-5 text-sm font-semibold">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save settings
      </button>}
    </div>
  );
};

const UsageCard = ({ title, u, limit }) => (
  <div className={card} data-testid={`ai-usage-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-stone-400">{title}</p>
    <p className="mt-2 text-xl text-stone-50">{usd(u.spent_usd)} <span className="text-xs text-stone-400">reserved (worst case){limit != null && ` of $${limit.toFixed(2)}`}</span></p>
    <p className="mt-1 text-sm text-stone-300">{usd(u.estimated_usd)} <span className="text-xs text-stone-400">estimated actual (from token counts)</span></p>
    <p className="mt-2 text-xs text-stone-400">{u.calls} AI calls · {u.messages} messages · {u.input_tokens.toLocaleString()} in / {u.output_tokens.toLocaleString()} out tokens</p>
  </div>
);

const Health = ({ days }) => {
  const avg = (sum, n) => (n ? `${(sum / n / 1000).toFixed(1)}s` : "–");
  return (
    <div className={`${card} overflow-x-auto`} data-testid="ai-admin-health">
      <p className="font-display text-base text-stone-100">Health, last 7 days</p>
      <p className="mt-1 text-xs text-stone-400">Totals only. Conversation content is never shown here.</p>
      <table className="mt-3 w-full min-w-[640px] text-left text-xs text-stone-300">
        <thead className="text-stone-400"><tr>{["Date", "Answered", "Errors", "Timeouts", "Blocked by limits", "Retries", "Replays", "Avg first reply", "Avg total", "Drafts"].map((h) => <th key={h} className="py-1.5 pr-3 font-normal">{h}</th>)}</tr></thead>
        <tbody>
          {days.length === 0 && <tr><td colSpan={10} className="py-3 text-stone-500">No AI activity yet.</td></tr>}
          {days.map((d) => (
            <tr key={d.date} className="border-t border-white/5">
              <td className="py-1.5 pr-3">{d.date}</td><td>{d.answered ?? 0}</td><td>{d.errors ?? 0}</td><td>{d.timeouts ?? 0}</td>
              <td>{["blocked_ceiling", "blocked_monthly", "blocked_daily", "blocked_messages"].reduce((a, k) => a + (d[k] ?? 0), 0)}</td>
              <td>{d.retries ?? 0}</td><td>{d.replayed ?? 0}</td><td>{avg(d.first_ms_sum, d.answered)}</td><td>{avg(d.total_ms_sum, d.answered)}</td><td>{d.drafts ?? 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Log = ({ items }) => (
  <div className={card} data-testid="ai-admin-log">
    <p className="font-display text-base text-stone-100">Settings change log</p>
    <ul className="mt-2 space-y-1 text-xs text-stone-400">
      {items.length === 0 && <li>No changes yet.</li>}
      {items.map((l, i) => (
        <li key={i}>{l.at?.slice(0, 16).replace("T", " ")} · {l.by}: {Object.entries(l.changes ?? {}).map(([k, [a, b]]) => `${k} ${a} → ${b}`).join(", ")}</li>
      ))}
    </ul>
  </div>
);

export const AiConcierge = () => {
  const [d, setD] = useState(null);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/concierge"); setD(data ?? null); } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return <p className="mt-6 text-sm text-stone-400">Loading AI Concierge…</p>;
  return (
    <div className="mt-6 space-y-4" data-testid="admin-ai-concierge">
      <Status d={d} />
      <Settings d={d} onSaved={load} />
      <div className="grid gap-4 md:grid-cols-3">
        <UsageCard title="Today" u={d.usage.today} limit={d.effective.limits.daily} />
        <UsageCard title="This month" u={d.usage.month} limit={d.effective.limits.monthly} />
        <UsageCard title="All time" u={d.usage.lifetime} limit={d.server.ceiling_usd} />
      </div>
      <Health days={Array.isArray(d.health) ? d.health : []} />
      <Log items={Array.isArray(d.settings_log) ? d.settings_log : []} />
      <p className="text-xs text-stone-400">{d.privacy.active_sessions} stored chats · deleted automatically after {d.privacy.retention_days} days · {d.published_entries} published knowledge entries</p>
      <div className="pt-4">
        <p className="font-display text-lg text-stone-100">Knowledge base</p>
        <KnowledgeBase />
      </div>
    </div>
  );
};
